import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Allocation } from '@ecinsa/shared';
import {
  ApiError, direccionApi, getToken, setToken,
  type AnalisisTiempos, type Desviacion, type OperarioTerminal, type Overview, type PlanDeReserva,
  type UsuarioTerminal,
} from '../lib/api';
import { BarrasDia, BarrasRanking, Grafica, Indicador, SERIE, SinDatos } from '../components/charts';
import { CambiarContrasena, Copiable, Dialog, Dot, Empty, Field, Pill } from '../components/ui';
import { Maestros } from './Maestros';
import { clockTime, hhmm, percent, qty, shortDate } from '../lib/format';

/**
 * Panel de dirección.
 *
 * Lo que quiere ver el responsable de planta desde su mesa: cómo va el día,
 * dónde se está yendo el tiempo, quién está trabajando y si algo se ha quedado
 * atascado camino de SAP. Se refresca solo.
 */
export function Direccion({ tenantName }: { tenantName: string }) {
  const [sesion, setSesion] = useState<{ user: UsuarioTerminal; tenant: { slug: string; name: string } } | null>(null);
  const [datos, setDatos] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dias, setDias] = useState(14);
  const [pestana, setPestana] = useState<'planta' | 'tiempos' | 'operarios' | 'maestros' | 'envios' | 'usuarios'>('planta');
  const [actualizado, setActualizado] = useState<Date | null>(null);

  const cargar = useCallback(async () => {
    try {
      const res = await direccionApi.overview(dias);
      setDatos(res);
      setActualizado(new Date());
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setToken(null);
        setSesion(null);
      } else setError((err as Error).message);
    }
  }, [dias]);

  useEffect(() => {
    if (!getToken()) return;
    direccionApi
      .me()
      .then(setSesion)
      .catch(() => {
        setToken(null);
        setSesion(null);
      });
  }, []);

  useEffect(() => {
    if (!sesion) return;
    void cargar();
    const t = setInterval(() => void cargar(), 20_000);
    return () => clearInterval(t);
  }, [sesion, cargar]);

  if (!sesion) {
    return <EntrarConUsuario tenantName={tenantName} onEntrar={setSesion} />;
  }

  return (
    <div className="app app--escritorio">
      <header className="topbar">
        <div>
          <div className="topbar__title">{sesion.tenant.name} · dirección</div>
          <div className="topbar__sub">
            {sesion.user.fullName} · {sesion.user.role === 'director' ? 'Responsable de planta' : sesion.user.role === 'jefe' ? 'Jefe de producción' : 'Consulta'}
          </div>
        </div>
        <div className="topbar__spacer" />
        {datos ? (
          <div className="conn">
            <Dot tone={datos.sap.conectado ? 'live' : 'danger'} />
            {datos.sap.conectado ? 'SAP conectado' : 'SAP no responde'}
          </div>
        ) : null}
        {actualizado ? <span className="muted small">Actualizado {clockTime(actualizado)}</span> : null}
        <TraerDeSap />
        <button
          className="btn btn--ghost"
          onClick={() => {
            setToken(null);
            setSesion(null);
          }}
        >
          Salir
        </button>
      </header>

      <div className="content stack">
        <div className="chips">
          {([['planta', 'Planta'], ['tiempos', 'Tiempos'], ['operarios', 'Operarios'], ['maestros', 'Datos maestros'], ['envios', 'Envíos a SAP'], ['usuarios', 'Usuarios']] as const).map(([k, l]) => (
            <button key={k} className={`chip${pestana === k ? ' chip--selected' : ''}`} onClick={() => setPestana(k)}>
              {l}
              {k === 'envios' && datos?.cola.failed ? ` · ${datos.cola.failed} con error` : ''}
            </button>
          ))}
          <div className="spacer" />
          {pestana === 'planta'
            ? ([[7, '7 días'], [30, '30 días'], [90, '3 meses'], [365, '1 año']] as const).map(([d, l]) => (
                <button key={d} className={`chip${dias === d ? ' chip--selected' : ''}`} onClick={() => setDias(d)}>
                  {l}
                </button>
              ))
            : null}
        </div>

        {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}
        {!datos ? <Empty icon="⏳" title="Cargando la situación de la planta…" /> : null}

        {datos && pestana === 'planta' ? (
          <Planta
            datos={datos}
            // Apartar material solo tiene sentido si el terminal va a consumir
            // de esa ubicación; con el parámetro apagado, no consume de ahí.
            puedeReservar={
              datos.ajustes.reservas && (sesion.user.role === 'director' || sesion.user.role === 'jefe')
            }
          />
        ) : null}
        {pestana === 'tiempos' ? (
          <Tiempos puedeCompletar={sesion.user.role === 'director' || sesion.user.role === 'jefe'} />
        ) : null}
        {pestana === 'operarios' ? (
          <Operarios puedeGestionar={sesion.user.role === 'director' || sesion.user.role === 'jefe'} />
        ) : null}
        {pestana === 'maestros' ? (
          <Maestros puedeGestionar={sesion.user.role === 'director' || sesion.user.role === 'jefe'} />
        ) : null}
        {datos && pestana === 'envios' ? <Envios puedeReintentar={sesion.user.role === 'director'} /> : null}
        {pestana === 'usuarios' ? <Usuarios rol={sesion.user.role} /> : null}
      </div>
    </div>
  );
}

/**
 * Trae de SAP los datos maestros y las OFs en el momento.
 *
 * El terminal ya lo hace solo cada minuto, pero cuando acabas de liberar una OF
 * o de tocar un recurso quieres verlo ya, y sin saber si ha llegado algo la
 * espera se hace rara. Por eso dice qué ha cambiado, no solo «hecho».
 */
function TraerDeSap() {
  const [estado, setEstado] = useState<'quieto' | 'trayendo'>('quieto');
  const [resultado, setResultado] = useState<{ ok: boolean; texto: string } | null>(null);

  const traer = async () => {
    setEstado('trayendo');
    setResultado(null);
    try {
      const r = await direccionApi.sincronizar();
      const cambio = (clave: 'empleados' | 'conPin' | 'ordenes' | 'fases' | 'causas', etiqueta: string) => {
        const dif = r.despues[clave] - r.antes[clave];
        return `${etiqueta}: ${r.despues[clave]}${dif !== 0 ? ` (${dif > 0 ? '+' : ''}${dif})` : ''}`;
      };
      setResultado({
        ok: !r.error,
        texto: r.error
          ? `SAP no respondió del todo: ${r.error}`
          : [cambio('ordenes', 'OFs liberadas'), cambio('empleados', 'empleados'), cambio('conPin', 'con PIN'),
             cambio('fases', 'fases'), cambio('causas', 'causas')].join(' · '),
      });
    } catch (err) {
      setResultado({ ok: false, texto: (err as Error).message });
    } finally {
      setEstado('quieto');
      setTimeout(() => setResultado(null), 9000);
    }
  };

  return (
    <>
      <button className="btn" disabled={estado === 'trayendo'} onClick={() => void traer()}>
        {estado === 'trayendo' ? 'Trayendo…' : '⟳ Traer de SAP'}
      </button>
      {resultado ? (
        <div className="toast-host">
          <div className={`toast ${resultado.ok ? 'toast--ok' : 'toast--error'}`}>
            <div className="toast__title">{resultado.ok ? 'Traído de SAP' : 'No se pudo traer todo'}</div>
            <div className="toast__body">{resultado.texto}</div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Planta({ datos, puedeReservar }: { datos: Overview; puedeReservar: boolean }) {
  const [reservando, setReservando] = useState<number | null>(null);
  const { oee, ahora, turno, periodo } = datos;
  const hayFichajes = turno.segundosProduciendo + turno.segundosParado > 0;

  const barrasDia = useMemo(
    () =>
      datos.produccionPorDia.map((d) => ({
        etiqueta: d.etiqueta,
        eje: d.etiqueta,
        segmentos: [
          { clave: 'Buenas', valor: d.good, color: SERIE.produciendo },
          { clave: 'Rechazadas', valor: d.scrap, color: SERIE.parado },
        ],
      })),
    [datos.produccionPorDia],
  );

  // Con histórico importado hay horas trabajadas aunque no haya recibos
  // declarados en el terminal: esa serie también merece verse.
  const barrasHoras = useMemo(
    () =>
      datos.produccionPorDia.map((d) => ({
        etiqueta: d.etiqueta,
        eje: d.etiqueta,
        segmentos: [{ clave: 'Tiempo imputado', valor: d.seconds, color: SERIE.produciendo }],
      })),
    [datos.produccionPorDia],
  );

  const porTramo = datos.agrupacion === 'dia' ? 'día' : datos.agrupacion === 'semana' ? 'semana' : 'mes';

  const barrasParadas = useMemo(
    () =>
      datos.paradas.map((p) => ({
        etiqueta: `${p.name} · ${p.count} ${p.count === 1 ? 'parada' : 'paradas'}`,
        segmentos: [{ clave: 'Tiempo parado', valor: p.seconds, color: SERIE.parado }],
      })),
    [datos.paradas],
  );

  const barrasOperarios = useMemo(
    () =>
      datos.operarios.map((o) => ({
        etiqueta: o.name,
        segmentos: [
          { clave: 'Produciendo', valor: o.runSeconds, color: SERIE.produciendo },
          { clave: 'Parado por incidencia', valor: o.stopSeconds, color: SERIE.parado },
        ],
      })),
    [datos.operarios],
  );

  return (
    <>
      <div className="eyebrow">Ahora mismo</div>
      <div className="kpi">
        <Indicador
          etiqueta="Operarios dentro"
          valor={String(ahora.dentro)}
          sub={`${ahora.imputando} imputando ahora mismo`}
          tono={ahora.dentro > 0 ? 'ok' : undefined}
        />
        <Indicador
          etiqueta="OFs en marcha"
          valor={String(ahora.ofsEnMarcha)}
          sub={`${datos.ordenes.rows.length} liberadas en total`}
        />
        <Indicador
          etiqueta="Incidencias abiertas"
          valor={String(ahora.incidenciasAbiertas)}
          tono={ahora.incidenciasAbiertas > 0 ? 'danger' : 'ok'}
          sub={ahora.incidenciasAbiertas > 0 ? 'Bloquean finalizar esas fases' : 'Ninguna fase bloqueada'}
        />
        <Indicador
          etiqueta="Pendiente de enviar a SAP"
          valor={String(datos.cola.pending)}
          tono={datos.cola.failed > 0 ? 'danger' : datos.cola.pending > 0 ? 'warn' : 'ok'}
          sub={datos.cola.failed > 0 ? `${datos.cola.failed} rechazados por SAP` : `${datos.cola.done} contabilizados`}
        />
        <Indicador
          etiqueta="Hoy"
          valor={hhmm(turno.segundosProduciendo)}
          sub={`${qty(turno.producido)} ud · ${turno.fasesFinalizadas} fases finalizadas`}
        />
      </div>

      <div className="eyebrow">
        En el periodo · {datos.ventanaDias === 365 ? 'último año' : `últimos ${datos.ventanaDias} días`}
      </div>
      <div className="kpi">
        <Indicador
          etiqueta="Horas imputadas"
          valor={hhmm(periodo.segundosProduciendo + periodo.segundosParado)}
          sub={
            periodo.segundosParado > 0
              ? `${hhmm(periodo.segundosParado)} parado por incidencia`
              : `${datos.operarios.length} operarios han imputado`
          }
          tono={periodo.segundosProduciendo > 0 ? 'ok' : undefined}
        />
        <Indicador
          etiqueta="Producido"
          valor={qty(periodo.producido)}
          sub={
            periodo.ordenesConProduccion > 0
              ? `en ${periodo.ordenesConProduccion} órdenes`
              : 'sin recibos de producción en el periodo'
          }
        />
        <Indicador
          etiqueta="Calidad"
          valor={periodo.producido + periodo.rechazado > 0 ? percent(periodo.producido / (periodo.producido + periodo.rechazado)) : '—'}
          proporcion={periodo.producido + periodo.rechazado > 0 ? periodo.producido / (periodo.producido + periodo.rechazado) : undefined}
          sub={periodo.rechazado > 0 ? `${qty(periodo.rechazado)} ud rechazadas` : 'sin rechazos declarados'}
          tono={periodo.rechazado > 0 ? 'warn' : undefined}
        />
        {periodo.hayPresencia ? (
          <Indicador
            etiqueta="Disponibilidad"
            valor={percent(oee.availability)}
            proporcion={oee.availability}
            sub="tiempo imputado sobre tiempo de presencia"
          />
        ) : (
          // Sin fichajes de entrada y salida no hay denominador: decirlo es
          // más útil que enseñar un 0 % que nadie puede interpretar.
          <Indicador
            etiqueta="Disponibilidad"
            valor="—"
            sub="hace falta que los operarios fichen entrada y salida"
          />
        )}
        <Indicador
          etiqueta="Fases finalizadas"
          valor={String(periodo.fasesFinalizadas)}
          sub={datos.tramosHistoricos > 0 ? `${datos.tramosHistoricos} tramos vienen del terminal clásico` : 'con el terminal nuevo'}
        />
      </div>

      <div className="grid-2">
        <Grafica
          titulo={`Horas trabajadas por ${porTramo}`}
          sub={
            datos.tramosHistoricos > 0
              ? `${hhmm(datos.segundosEnVentana)} en el periodo · incluye ${datos.tramosHistoricos} tramos del terminal clásico`
              : `${hhmm(datos.segundosEnVentana)} imputadas en el periodo`
          }
        >
          <BarrasDia
            barras={barrasHoras}
            series={[{ clave: 'Tiempo imputado', color: SERIE.produciendo }]}
            formato={(v) => hhmm(v)}
          />
        </Grafica>

        <Grafica titulo={`Producción por ${porTramo}`} sub={`Unidades recibidas en los últimos ${datos.ventanaDias} días`}>
          <BarrasDia
            barras={barrasDia}
            series={[
              { clave: 'Buenas', color: SERIE.produciendo },
              { clave: 'Rechazadas', color: SERIE.parado },
            ]}
            formato={(v) => `${qty(v)} ud`}
          />
          <div style={{ marginTop: 12 }}>
            <LeyendaInline />
          </div>
        </Grafica>
      </div>

      <div className="grid-2">
        <Grafica titulo="Dónde se va el tiempo" sub={`Paradas con causa en los últimos ${datos.ventanaDias} días`}>
          <BarrasRanking
            barras={barrasParadas}
            series={[{ clave: 'Tiempo parado', color: SERIE.parado }]}
            formato={(v) => hhmm(v)}
            vacio="Ninguna parada con causa en este periodo. Es una buena noticia."
          />
        </Grafica>
      </div>

      <Grafica titulo="Tiempo por operario" sub={`Últimos ${datos.ventanaDias} días`}>
        <BarrasRanking
          barras={barrasOperarios}
          series={[
            { clave: 'Produciendo', color: SERIE.produciendo },
            { clave: 'Parado por incidencia', color: SERIE.parado },
          ]}
          formato={(v) => hhmm(v)}
          vacio={hayFichajes ? 'Sin imputaciones en este periodo.' : 'Todavía no hay ningún fichaje: en cuanto los operarios empiecen a imputar, aquí se verá el reparto del tiempo.'}
        />
      </Grafica>

      <Grafica titulo="Órdenes de fabricación" sub="Situación en tiempo real">
        {datos.ordenes.rows.length === 0 ? (
          <SinDatos texto="No hay OFs liberadas en SAP." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table--zebra">
              <thead>
                <tr>
                  <th />
                  <th>OF</th>
                  <th>Artículo</th>
                  <th>Fases</th>
                  <th>Avance</th>
                  <th>Tiempo</th>
                  <th>Entrega</th>
                  {puedeReservar ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {datos.ordenes.rows.slice(0, 12).map((r) => (
                  <tr key={r.docEntry}>
                    <td>
                      <Dot tone={r.incident ? 'danger' : r.delivery === 'late' ? 'danger' : r.delivery === 'soon' ? 'warn' : 'ok'} />
                    </td>
                    <td><strong>{r.docNum}</strong></td>
                    <td>
                      {r.itemDescription}
                      <div className="small muted">{r.itemCode}</div>
                    </td>
                    <td>
                      <div className="row row--wrap" style={{ gap: 6 }}>
                        {r.phases.map((f) => (
                          <span key={f.code} className={`pill${f.state === 'running' ? ' pill--ok' : f.state === 'done' ? ' pill--accent' : ''}`}>
                            {f.code}
                            {f.operators.length ? ` · ${f.operators.join(', ')}` : ''}
                          </span>
                        ))}
                        {r.incident ? <span className="pill pill--danger">⚠ {r.incident.name}</span> : null}
                      </div>
                    </td>
                    <td className="mono">
                      {qty(r.completedQuantity)}/{qty(r.plannedQuantity)}
                      <div className="bar">
                        <div className="bar__fill" style={{ width: `${Math.min(100, (r.completedQuantity / Math.max(1, r.plannedQuantity)) * 100)}%` }} />
                      </div>
                    </td>
                    <td className="mono small">{hhmm(r.laborSeconds)}</td>
                    <td className="small">{shortDate(r.dueDate)}</td>
                    {puedeReservar ? (
                      <td>
                        <button className="btn btn--ghost" onClick={() => setReservando(r.docEntry)}>
                          Reservar
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Grafica>

      {reservando !== null ? <Reservar docEntry={reservando} onClose={() => setReservando(null)} /> : null}

      <p className="muted small">
        Las horas salen de los fichajes del terminal; las unidades producidas, de los recibos ya contabilizados en SAP.
        La disponibilidad necesita además fichajes de entrada y salida, que el terminal clásico no guardaba.
      </p>
    </>
  );
}

function LeyendaInline() {
  return (
    <div className="row row--wrap small" style={{ gap: 16 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <span style={{ width: 11, height: 11, borderRadius: 3, background: SERIE.produciendo }} />
        <span className="muted">Buenas</span>
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <span style={{ width: 11, height: 11, borderRadius: 3, background: SERIE.parado }} />
        <span className="muted">Rechazadas</span>
      </span>
    </div>
  );
}

/**
 * Operarios del terminal.
 *
 * Dar de alta un operario no es crear a nadie: el empleado ya está en SAP. Lo
 * que falta es su PIN, y se escribe en su ficha de SAP para que el dato viva en
 * un solo sitio.
 */
type ColumnaOperario = 'nombre' | 'numero' | 'pin' | 'recursos' | 'estado';
type Filtro = 'todos' | 'listos' | 'sinPin' | 'sinRecurso' | 'jefes';

const FILTROS: { clave: Filtro; etiqueta: string }[] = [
  { clave: 'todos', etiqueta: 'Todos' },
  { clave: 'listos', etiqueta: 'Listos para imputar' },
  { clave: 'sinPin', etiqueta: 'Sin PIN' },
  { clave: 'sinRecurso', etiqueta: 'Sin recurso' },
  { clave: 'jefes', etiqueta: 'Jefes de producción' },
];

/**
 * Operarios del terminal.
 *
 * Dar de alta un operario no es crear a nadie: el empleado ya está en SAP. Lo
 * que falta es su PIN, y se escribe en su ficha de SAP para que el dato viva en
 * un solo sitio.
 */
/**
 * Tiempo previsto contra tiempo real.
 *
 * Es la pantalla que el terminal clásico nunca tuvo: grababa las horas y ahí
 * se acababa. La ruta de SAP dice lo que cada fase debía costar por unidad; el
 * terminal sabe lo que costó. Cruzarlo señala qué está mal escandallado, que
 * es lo que se arregla una vez y deja de mentir en todos los costes.
 */
function Tiempos({ puedeCompletar }: { puedeCompletar: boolean }) {
  const [dias, setDias] = useState(180);
  const [datos, setDatos] = useState<AnalisisTiempos | null>(null);
  const [vista, setVista] = useState<'articulo' | 'fase' | 'recurso' | 'ofs'>('articulo');
  const [orden, setOrden] = useState<{ columna: ColumnaTiempo; asc: boolean }>({ columna: 'desviacion', asc: false });
  const [error, setError] = useState<string | null>(null);
  const [completando, setCompletando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(() => {
    setDatos(null);
    direccionApi
      .tiempos(dias)
      .then(setDatos)
      .catch((e: Error) => setError(e.message));
  }, [dias]);
  useEffect(cargar, [cargar]);

  const completar = async () => {
    setCompletando(true);
    setError(null);
    try {
      const r = await direccionApi.completarTiempos();
      setAviso(
        r.traidas === 0
          ? 'No había ninguna OF que traer.'
          : `Traídas ${r.traidas} OFs de SAP${r.noEncontradas ? `; ${r.noEncontradas} ya no existen allí` : ''}.`,
      );
      cargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setCompletando(false);
    }
  };

  const filas: Desviacion[] = useMemo(() => {
    if (!datos) return [];
    const base =
      vista === 'articulo' ? datos.porArticulo
      : vista === 'fase' ? datos.porFase
      : vista === 'recurso' ? datos.porRecurso
      : datos.ofs;
    const valor = (d: Desviacion): number | string => {
      switch (orden.columna) {
        case 'ofs': return d.ofs;
        case 'unidades': return d.unidades;
        case 'previsto': return d.previsto;
        case 'real': return d.real;
        case 'parado': return d.parado;
        case 'porcentaje': return d.previsto > 0 ? (d.real - d.previsto) / d.previsto : 0;
        case 'rutaUnidad': return d.unidades > 0 ? d.previsto / d.unidades : 0;
        case 'realUnidad': return d.unidades > 0 ? d.real / d.unidades : 0;
        case 'desviacion': return d.real - d.previsto;
        default: return d.etiqueta.toLocaleLowerCase('es');
      }
    };
    return [...base].sort((a, b) => {
      const va = valor(a);
      const vb = valor(b);
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'es');
      return orden.asc ? cmp : -cmp;
    });
  }, [datos, vista, orden]);

  const barras = useMemo(
    () =>
      (datos?.porArticulo ?? [])
        .filter((d) => d.real > d.previsto)
        .slice(0, 10)
        .map((d) => ({
          etiqueta: d.etiqueta,
          segmentos: [
            { clave: 'Previsto', valor: d.previsto, color: SERIE.produciendo },
            { clave: 'De más', valor: d.real - d.previsto, color: SERIE.parado },
          ],
        })),
    [datos],
  );

  if (error && !datos) return <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div>;
  if (!datos) return <Empty icon="⏳" title="Comparando tiempos…" />;

  const { resumen } = datos;
  const desviacion = resumen.real - resumen.previsto;
  const ratio = resumen.previsto > 0 ? desviacion / resumen.previsto : 0;
  const sinComparar = resumen.tramos - resumen.comparados;

  const cabecera = (columna: ColumnaTiempo, etiqueta: string, ancho?: number) => (
    <th style={ancho ? { width: ancho } : undefined}>
      <button
        className={`th-orden${orden.columna === columna ? ' th-orden--activa' : ''}`}
        onClick={() => setOrden((o) => (o.columna === columna ? { columna, asc: !o.asc } : { columna, asc: false }))}
      >
        {etiqueta}
        <span aria-hidden>{orden.columna === columna ? (orden.asc ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );

  return (
    <>
      <div className="row row--wrap">
        <div>
          <div className="eyebrow">Previsto contra real</div>
          <div className="muted small">
            {resumen.comparados} de {resumen.tramos} tramos comparados · {resumen.ofs} OFs · {qty(resumen.unidades)} uds
          </div>
        </div>
        <div className="spacer" />
        <div className="chips">
          {([[90, '3 meses'], [180, '6 meses'], [365, '1 año'], [1095, '3 años']] as const).map(([d, l]) => (
            <button key={d} className={`chip${dias === d ? ' chip--selected' : ''}`} onClick={() => setDias(d)}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="kpi">
        <Indicador etiqueta="Horas previstas" valor={hhmm(resumen.previsto)} sub="Lo que dice la ruta de SAP para lo producido" />
        <Indicador etiqueta="Horas reales" valor={hhmm(resumen.real)} sub="Imputadas, sin contar paradas con causa" />
        <Indicador
          etiqueta="Desviación"
          valor={`${desviacion >= 0 ? '+' : '−'}${hhmm(Math.abs(desviacion))}`}
          sub={`${desviacion >= 0 ? 'de más' : 'de menos'} que lo previsto`}
          tono={ratio > 0.1 ? 'danger' : ratio > 0.02 ? 'warn' : 'ok'}
          proporcion={Math.min(1, Math.abs(ratio))}
        />
        <Indicador
          etiqueta="Paradas con causa"
          valor={hhmm(resumen.parado)}
          sub="Aparte de lo anterior: no es culpa del escandallo"
          tono={resumen.parado > 0 ? 'warn' : undefined}
        />
      </div>

      {datos.ofsSinFicha > 0 ? (
        <div className="card row row--wrap" style={{ gap: 12, alignItems: 'center' }}>
          <div>
            <strong>{datos.ofsSinFicha} OFs con horas imputadas no tienen su ficha aquí.</strong>
            <div className="muted small">
              Son OFs cerradas en SAP, que es justo donde está el histórico. Sin su ruta no hay con qué comparar.
            </div>
          </div>
          <div className="spacer" />
          {puedeCompletar ? (
            <button className="btn btn--primary" onClick={() => void completar()} disabled={completando}>
              {completando ? 'Trayendo de SAP…' : 'Traerlas de SAP'}
            </button>
          ) : (
            <span className="muted small">Lo puede hacer el jefe de producción.</span>
          )}
        </div>
      ) : null}

      {aviso ? <div className="toast" style={{ boxShadow: 'none' }}>{aviso}</div> : null}
      {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}

      <Grafica
        titulo="Dónde se van las horas de más"
        sub="Artículo y fase, ordenados por las horas que exceden lo previsto"
      >
        <BarrasRanking
          barras={barras}
          series={[{ clave: 'Previsto', color: SERIE.produciendo }, { clave: 'De más', color: SERIE.parado }]}
          formato={hhmm}
          vacio="Ninguna fase se pasa de lo previsto en este periodo."
        />
      </Grafica>

      <section className="card stack">
        <div className="row row--wrap">
          <div className="chips">
            {([['articulo', 'Por artículo y fase'], ['fase', 'Por fase'], ['recurso', 'Por recurso'], ['ofs', 'OFs concretas']] as const).map(
              ([k, l]) => (
                <button key={k} className={`chip${vista === k ? ' chip--selected' : ''}`} onClick={() => setVista(k)}>
                  {l}
                </button>
              ),
            )}
          </div>
          <div className="spacer" />
          <span className="muted small" style={{ alignSelf: 'center' }}>{filas.length} filas</span>
        </div>

        <p className="muted small" style={{ margin: 0 }}>
          Las dos columnas por unidad son las que sirven para corregir la ruta en SAP: si «Ruta h/ud» vale 1 h clavado
          en todas las fases, es que nadie ha mantenido los tiempos previstos y la desviación no dice nada del taller,
          dice que la ruta está sin rellenar. «Real h/ud» es lo que hay que escribir allí.
        </p>

        {vista === 'recurso' && datos.tramosSinRecurso > 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            {datos.tramosSinRecurso} tramos no dicen en qué recurso se trabajó: son los que importamos del terminal
            clásico, que no lo guardaba. Salen en las otras vistas, no en esta.
          </p>
        ) : null}

        <div style={{ overflowX: 'auto' }}>
          <table className="table table--zebra">
            <thead>
              <tr>
                {cabecera('etiqueta', vista === 'ofs' ? 'OF y fase' : vista === 'fase' ? 'Fase' : vista === 'recurso' ? 'Recurso' : 'Artículo y fase')}
                {cabecera('ofs', 'OFs')}
                {cabecera('unidades', 'Uds')}
                {cabecera('rutaUnidad', 'Ruta h/ud')}
                {cabecera('realUnidad', 'Real h/ud')}
                {cabecera('previsto', 'Previsto')}
                {cabecera('real', 'Real')}
                {cabecera('desviacion', 'Desviación')}
                {cabecera('porcentaje', '%')}
                {cabecera('parado', 'Paradas')}
              </tr>
            </thead>
            <tbody>
              {filas.map((d) => {
                const desv = d.real - d.previsto;
                const pct = d.previsto > 0 ? desv / d.previsto : 0;
                const tono = pct > 0.1 ? 'var(--danger)' : pct > 0.02 ? 'var(--warn)' : 'var(--ok)';
                return (
                  <tr key={d.clave}>
                    <td>
                      {d.etiqueta}
                      {d.sub ? <div className="small muted">{d.sub}</div> : null}
                    </td>
                    <td className="mono">{d.ofs}</td>
                    <td className="mono">{qty(d.unidades)}</td>
                    <td className="mono muted">{d.unidades > 0 ? hhmm(d.previsto / d.unidades) : '—'}</td>
                    <td className="mono">{d.unidades > 0 ? hhmm(d.real / d.unidades) : '—'}</td>
                    <td className="mono">{hhmm(d.previsto)}</td>
                    <td className="mono">{hhmm(d.real)}</td>
                    <td className="mono" style={{ color: tono }}>
                      {desv >= 0 ? '+' : '−'}
                      {hhmm(Math.abs(desv))}
                    </td>
                    <td className="mono" style={{ color: tono }}>
                      {desv >= 0 ? '+' : '−'}
                      {percent(Math.abs(pct))}
                    </td>
                    <td className="mono muted">{d.parado > 0 ? hhmm(d.parado) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filas.length === 0 ? (
          <SinDatos texto="No hay nada que comparar todavía en este periodo." />
        ) : null}
      </section>

      {sinComparar > 0 ? (
        <section className="card stack">
          <div className="eyebrow">Lo que no se ha podido comparar</div>
          <p className="muted small" style={{ margin: 0 }}>
            {sinComparar} de {resumen.tramos} tramos se han dejado fuera. Se dicen porque un total que esconde la mitad
            de las horas no sirve para decidir nada.
          </p>
          <table className="table">
            <tbody>
              {datos.excluidos.map((e) => (
                <tr key={e.motivo}>
                  <td>{e.motivo}</td>
                  <td className="mono" style={{ width: 110 }}>{e.pares} tramos</td>
                  <td className="mono muted" style={{ width: 110 }}>{hhmm(e.segundos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </>
  );
}

type ColumnaTiempo =
  | 'etiqueta' | 'ofs' | 'unidades' | 'rutaUnidad' | 'realUnidad'
  | 'previsto' | 'real' | 'desviacion' | 'porcentaje' | 'parado';

/**
 * Apartar material para una OF.
 *
 * Es la otra mitad del parámetro de reservas. Con él activado, el terminal
 * consume los componentes de la ubicación que lleva el nombre de la OF en vez
 * de preguntárselos al operario; si nadie los ha llevado allí antes, el
 * operario se encuentra la pantalla vacía y no puede finalizar la fase.
 */
function Reservar({ docEntry, onClose }: { docEntry: number; onClose: () => void }) {
  const [plan, setPlan] = useState<PlanDeReserva | null>(null);
  const [cantidades, setCantidades] = useState<Record<number, string>>({});
  const [reparto, setReparto] = useState<Record<number, Allocation[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    direccionApi
      .planDeReserva(docEntry)
      .then((p) => {
        setPlan(p);
        // Lo pendiente es lo que casi siempre se quiere apartar: se propone.
        setCantidades(Object.fromEntries(p.componentes.map((c) => [c.lineNum, String(c.pendiente || '')])));
        setReparto(
          Object.fromEntries(
            p.componentes.map((c) => [
              c.lineNum,
              repartirPorDisponibilidad(c.disponible, c.pendiente, c.managedBy),
            ]),
          ),
        );
      })
      .catch((e: Error) => setError(e.message));
  }, [docEntry]);

  const enviar = async () => {
    if (!plan) return;
    setEnviando(true);
    setError(null);
    try {
      const lineas = plan.componentes
        .map((c) => ({
          lineNum: c.lineNum,
          quantity: Number(cantidades[c.lineNum] ?? 0) || 0,
          allocations: reparto[c.lineNum] ?? [],
        }))
        .filter((l) => l.quantity > 0);
      const r = await direccionApi.reservar(docEntry, lineas);
      setHecho(`Traslado encolado a la ubicación ${r.ubicacion} con ${r.lineas} componentes.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog
      title={plan ? `Reservar material · OF ${plan.docNum}` : 'Reservar material'}
      subtitle={plan ? `${plan.itemDescription} · almacén ${plan.warehouse}` : undefined}
      onClose={onClose}
      wide
    >
      {!plan ? (
        error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : <Empty icon="⏳" title="Leyendo los componentes…" />
      ) : (
        <div className="stack">
          {!plan.activa ? (
            <div className="toast" style={{ boxShadow: 'none' }}>
              El parámetro «Reservas de componentes por ubicación» está desactivado. Puedes apartar material igualmente,
              pero el terminal seguirá pidiendo los consumos al operario en vez de tomarlos de aquí.
            </div>
          ) : null}

          <p className="muted small" style={{ margin: 0 }}>
            El material se traslada a la ubicación <span className="mono">{plan.ubicacion}</span> del almacén{' '}
            {plan.warehouse}
            {plan.ubicacionExiste ? '.' : ', que se creará en SAP al confirmar.'}
          </p>

          {plan.componentes.length === 0 ? (
            <Empty icon="📦" title="La OF no tiene componentes que apartar" />
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: '46vh', overflowY: 'auto' }}>
              <table className="table table--zebra">
                <thead>
                  <tr>
                    <th>Componente</th>
                    <th>Necesario</th>
                    <th>Ya reservado</th>
                    <th>Apartar ahora</th>
                    <th>De dónde sale</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.componentes.map((c) => (
                    <tr key={c.lineNum}>
                      <td>
                        {c.description}
                        <div className="small muted mono">{c.itemCode}</div>
                      </td>
                      <td className="mono">{qty(c.necesario)}</td>
                      <td className="mono">
                        {qty(c.reservado)}
                        {c.pendiente <= 0 && c.necesario > 0 ? <> <Pill tone="ok">Completo</Pill></> : null}
                      </td>
                      <td>
                        <input
                          className="input input--qty"
                          style={{ width: 110 }}
                          inputMode="decimal"
                          value={cantidades[c.lineNum] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCantidades((s) => ({ ...s, [c.lineNum]: v }));
                            setReparto((s) => ({
                              ...s,
                              [c.lineNum]: repartirPorDisponibilidad(c.disponible, Number(v) || 0, c.managedBy),
                            }));
                          }}
                        />
                      </td>
                      <td className="small">
                        {c.managedBy === 'none' ? (
                          <span className="muted">Sin lote ni serie</span>
                        ) : (reparto[c.lineNum] ?? []).length === 0 ? (
                          <span style={{ color: 'var(--warn)' }}>
                            Sin existencias con {c.managedBy === 'serial' ? 'serie' : 'lote'} fuera de la reserva
                          </span>
                        ) : (
                          <div className="stack" style={{ gap: 2 }}>
                            {(reparto[c.lineNum] ?? []).map((a, i) => (
                              <span key={`${a.code}-${i}`} className="mono small">
                                {a.code} · {qty(a.quantity)}
                                {a.binCode ? ` · ${a.binCode}` : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}
          {hecho ? <div className="toast" style={{ boxShadow: 'none' }}>{hecho}</div> : null}

          <div className="row">
            <p className="muted small" style={{ margin: 0 }}>
              El reparto por lotes se propone por orden de disponibilidad. El traslado se contabiliza en SAP desde la
              cola de envíos: si algo falla, sale ahí con su motivo y se puede reintentar.
            </p>
            <div className="spacer" />
            <button className="btn" onClick={onClose}>
              {hecho ? 'Cerrar' : 'Cancelar'}
            </button>
            <button className="btn btn--primary btn--lg" onClick={() => void enviar()} disabled={enviando || hecho !== null}>
              {enviando ? 'Encolando…' : 'Apartar material'}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/**
 * Propone de qué lotes sale la cantidad, de mayor disponibilidad a menor.
 *
 * Una serie no se reparte: cada número es una pieza, así que se toman tantos
 * números como unidades.
 */
function repartirPorDisponibilidad(
  disponible: PlanDeReserva['componentes'][number]['disponible'],
  cantidad: number,
  managedBy: 'none' | 'batch' | 'serial',
): Allocation[] {
  if (managedBy === 'none' || cantidad <= 0) return [];
  const orden = [...disponible].sort((a, b) => b.available - a.available);
  const salida: Allocation[] = [];
  let pendiente = cantidad;
  for (const fila of orden) {
    if (pendiente <= 1e-6) break;
    const toma = managedBy === 'serial' ? 1 : Math.min(pendiente, fila.available);
    if (toma <= 0) continue;
    salida.push({ code: fila.code, quantity: toma, binAbsEntry: fila.binAbsEntry, binCode: fila.binCode });
    pendiente -= toma;
  }
  return salida;
}

function Operarios({ puedeGestionar }: { puedeGestionar: boolean }) {
  const [datos, setDatos] = useState<Awaited<ReturnType<typeof direccionApi.operarios>> | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [orden, setOrden] = useState<{ columna: ColumnaOperario; asc: boolean }>({ columna: 'nombre', asc: true });
  const [ocupado, setOcupado] = useState<number | null>(null);
  const [credencial, setCredencial] = useState<{ nombre: string; pin: string; sobrescrito: string | null } | null>(null);
  const [manual, setManual] = useState<OperarioTerminal | null>(null);
  const [alta, setAlta] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(
    () => void direccionApi.operarios().then(setDatos).catch((e: Error) => setError(e.message)),
    [],
  );
  useEffect(cargar, [cargar]);

  const asignar = async (op: OperarioTerminal, pin?: string) => {
    setOcupado(op.employeeId);
    setError(null);
    try {
      const r = await direccionApi.asignarPin(op.employeeId, pin);
      setCredencial({ nombre: op.fullName, pin: r.pin, sobrescrito: r.sobrescrito });
      setManual(null);
      cargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setOcupado(null);
    }
  };

  /** Al pulsar una columna se ordena por ella; al repetir, se invierte. */
  const ordenarPor = (columna: ColumnaOperario) =>
    setOrden((o) => (o.columna === columna ? { columna, asc: !o.asc } : { columna, asc: true }));

  const lista = useMemo(() => {
    if (!datos) return [];
    const texto = busqueda.trim().toLowerCase();
    const filtrada = datos.operarios.filter((o) => {
      if (filtro === 'listos' && o.faltan.length > 0) return false;
      if (filtro === 'sinPin' && o.hasPin) return false;
      if (filtro === 'sinRecurso' && o.resourceCodes.length > 0) return false;
      if (filtro === 'jefes' && !o.supervisor) return false;
      if (!texto) return true;
      return (
        o.fullName.toLowerCase().includes(texto) ||
        String(o.employeeId).includes(texto) ||
        (o.jobTitle ?? '').toLowerCase().includes(texto) ||
        o.resourceCodes.join(' ').toLowerCase().includes(texto)
      );
    });

    const valor = (o: OperarioTerminal): string | number => {
      switch (orden.columna) {
        case 'numero': return o.employeeId;
        case 'pin': return o.hasPin ? 0 : 1;
        case 'recursos': return o.resourceCodes.join(', ') || 'zzz';
        // «Estado» se ordena por lo que falta, no alfabéticamente: primero los
        // que ya pueden trabajar, y los que más les falta al final.
        case 'estado': return o.faltan.length;
        default: return o.fullName.toLocaleLowerCase('es');
      }
    };

    return [...filtrada].sort((a, b) => {
      const va = valor(a);
      const vb = valor(b);
      let cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'es');
      // Empates: siempre por nombre, para que el orden no baile entre cargas.
      if (cmp === 0) cmp = a.fullName.localeCompare(b.fullName, 'es');
      return orden.asc ? cmp : -cmp;
    });
  }, [datos, busqueda, filtro, orden]);

  if (!datos) return <Empty icon="⏳" title="Cargando empleados…" />;

  const conPin = datos.operarios.filter((o) => o.hasPin).length;
  const listos = datos.operarios.filter((o) => o.faltan.length === 0).length;
  const cabecera = (columna: ColumnaOperario, etiqueta: string) => (
    <th>
      <button className={`th-orden${orden.columna === columna ? ' th-orden--activa' : ''}`} onClick={() => ordenarPor(columna)}>
        {etiqueta}
        <span aria-hidden>{orden.columna === columna ? (orden.asc ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );

  return (
    <section className="card stack">
      <div className="row row--wrap">
        <div>
          <div className="eyebrow">Empleados de SAP</div>
          <div className="muted small">
            {datos.operarios.length} activos · {conPin} con PIN · {listos} listos para imputar
          </div>
        </div>
        <div className="spacer" />
        <input
          className="input"
          style={{ width: 280 }}
          placeholder="Buscar por nombre, nº, puesto o recurso"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        {puedeGestionar ? (
          <button className="btn btn--primary" onClick={() => setAlta(true)}>
            + Nuevo empleado
          </button>
        ) : null}
      </div>

      <div className="chips">
        {FILTROS.map((f) => (
          <button
            key={f.clave}
            className={`chip${filtro === f.clave ? ' chip--selected' : ''}`}
            style={{ minHeight: 44 }}
            onClick={() => setFiltro(f.clave)}
          >
            {f.etiqueta}
          </button>
        ))}
        <div className="spacer" />
        <span className="muted small" style={{ alignSelf: 'center' }}>
          {lista.length === datos.operarios.length
            ? `${lista.length} empleados`
            : `${lista.length} de ${datos.operarios.length}`}
        </span>
      </div>

      <p className="muted small" style={{ margin: 0 }}>
        El PIN se guarda en el campo <span className="mono">{datos.campoPin}</span> de la ficha del empleado en SAP, no
        aquí. Para que alguien pueda imputar tiempos necesita además estar asignado a un recurso de mano de obra en SAP
        (Recursos → pestaña Empleados), que es lo que decide en qué fases puede trabajar.
      </p>

      {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}

      <div style={{ overflowX: 'auto' }}>
        <table className="table table--zebra">
          <thead>
            <tr>
              {cabecera('nombre', 'Empleado')}
              {cabecera('numero', 'Nº')}
              {cabecera('pin', 'PIN')}
              {cabecera('recursos', 'Recursos de mano de obra')}
              {cabecera('estado', 'Estado')}
              {puedeGestionar ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {lista.map((op) => (
              <tr key={op.employeeId}>
                <td>
                  {op.fullName}
                  {op.supervisor ? <> <Pill tone="accent">Jefe de producción</Pill></> : null}
                  <div className="small muted">{op.jobTitle ?? ''}</div>
                </td>
                <td className="mono">{op.employeeId}</td>
                <td>{op.hasPin ? <Pill tone="ok">Asignado</Pill> : <Pill tone="warn">Sin PIN</Pill>}</td>
                <td className="small mono">{op.resourceCodes.length ? op.resourceCodes.join(', ') : <span className="muted">ninguno</span>}</td>
                <td className="small">
                  {op.faltan.length === 0 ? (
                    <span style={{ color: 'var(--ok)' }}>Puede usar el terminal</span>
                  ) : (
                    <span style={{ color: 'var(--warn)' }}>Falta {op.faltan.join(' y ')}</span>
                  )}
                </td>
                {puedeGestionar ? (
                  <td>
                    <div className="row">
                      <button className="btn" disabled={ocupado === op.employeeId} onClick={() => void asignar(op)}>
                        {ocupado === op.employeeId ? '…' : op.hasPin ? 'Nuevo PIN' : 'Generar PIN'}
                      </button>
                      <button className="btn btn--ghost" disabled={ocupado === op.employeeId} onClick={() => setManual(op)}>
                        Escribirlo
                      </button>
                      {op.hasPin ? (
                        <button
                          className="btn btn--ghost"
                          disabled={ocupado === op.employeeId}
                          onClick={() => {
                            setOcupado(op.employeeId);
                            void direccionApi
                              .quitarPin(op.employeeId)
                              .then(cargar)
                              .catch((e: Error) => setError(e.message))
                              .finally(() => setOcupado(null));
                          }}
                        >
                          Quitar
                        </button>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
        {lista.length === 0 ? <SinDatos texto="Ningún empleado coincide con el filtro." /> : null}
      </div>

      {manual ? (
        <PinManual operario={manual} onClose={() => setManual(null)} onGuardar={(pin) => void asignar(manual, pin)} />
      ) : null}

      {alta ? (
        <AltaEmpleado
          onClose={() => setAlta(false)}
          onCreado={(nombre, pin, avisos) => {
            setAlta(false);
            cargar();
            setCredencial({ nombre, pin, sobrescrito: null });
            if (avisos.length) setError(avisos.join(' · '));
          }}
        />
      ) : null}

      {credencial ? (
        <Dialog title={`PIN de ${credencial.nombre}`} onClose={() => setCredencial(null)}>
          <div className="stack">
            <div className="card card--flat">
              <Copiable etiqueta="PIN para el terminal" valor={credencial.pin} grande />
            </div>
            <p className="muted small" style={{ margin: 0 }}>
              Ya está guardado en su ficha de SAP y funciona en el terminal ahora mismo. Aquí no se puede volver a
              consultar: en esta base solo queda su huella. Si se pierde, genera otro.
            </p>
            {credencial.sobrescrito ? (
              <div className="toast" style={{ boxShadow: 'none', borderColor: 'rgba(251,191,36,.5)' }}>
                ⚠️ El campo tenía antes el valor <span className="mono">{credencial.sobrescrito}</span> y se ha
                sustituido. Si ese campo se usaba para otra cosa, avisa a administración.
              </div>
            ) : null}
            <button className="btn btn--primary btn--lg" onClick={() => setCredencial(null)}>Hecho</button>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

/**
 * Alta de un empleado nuevo.
 *
 * Lo crea EN SAP: a partir de aquí existe para toda la empresa, no solo para el
 * terminal. Por eso se piden los mismos mínimos que pide la ficha de SAP y se
 * ofrece dejarlo listo para trabajar de una vez: PIN, recurso y, si procede,
 * rol de jefe.
 */
function AltaEmpleado({
  onClose,
  onCreado,
}: {
  onClose: () => void;
  onCreado: (nombre: string, pin: string, avisos: string[]) => void;
}) {
  const [catalogos, setCatalogos] = useState<Awaited<ReturnType<typeof direccionApi.catalogosEmpleado>> | null>(null);
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [puesto, setPuesto] = useState('');
  const [departamento, setDepartamento] = useState<number | ''>('');
  const [sucursal, setSucursal] = useState<number | ''>('');
  const [pin, setPin] = useState('');
  const [recurso, setRecurso] = useState('');
  const [esJefe, setEsJefe] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    direccionApi.catalogosEmpleado().then(setCatalogos).catch((e: Error) => setError(e.message));
  }, []);

  const crear = async () => {
    setGuardando(true);
    setError(null);
    try {
      const r = await direccionApi.crearEmpleado({
        firstName: nombre,
        lastName: apellidos,
        jobTitle: puesto || null,
        department: departamento === '' ? null : departamento,
        branch: sucursal === '' ? null : sucursal,
        ...(pin ? { pin } : {}),
        resourceCode: recurso || null,
        esJefe,
      });
      onCreado(`${nombre} ${apellidos}`, r.pin, r.avisos);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setGuardando(false);
    }
  };

  const sinRolJefe = !catalogos?.rolJefe;

  return (
    <Dialog
      title="Nuevo empleado"
      subtitle="Se crea en SAP (Recursos Humanos → Datos maestros de empleado), no solo en el terminal."
      onClose={onClose}
      wide
    >
      <div className="stack">
        <div className="grid-2">
          <Field label="Nombre"><input className="input" autoFocus value={nombre} onChange={(e) => setNombre(e.target.value)} /></Field>
          <Field label="Apellidos"><input className="input" value={apellidos} onChange={(e) => setApellidos(e.target.value)} /></Field>
        </div>
        <div className="grid-2">
          <Field label="Puesto (opcional)"><input className="input" value={puesto} onChange={(e) => setPuesto(e.target.value)} placeholder="Operario de mecanizado" /></Field>
          <Field label="Departamento">
            <select className="input" value={departamento} onChange={(e) => setDepartamento(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">General</option>
              {(catalogos?.departments ?? []).filter((d) => d.code !== -2).map((d) => (
                <option key={d.code} value={d.code}>{d.name}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Sucursal">
            <select className="input" value={sucursal} onChange={(e) => setSucursal(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">Principal</option>
              {(catalogos?.branches ?? []).filter((b) => b.code !== -2).map((b) => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </select>
          </Field>
          <Field label="PIN del terminal (vacío = se genera uno)">
            <input
              className="input mono"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 15))}
              placeholder="se genera solo"
            />
          </Field>
        </div>

        <Field label="Recurso de mano de obra (es lo que le deja imputar)">
          <select className="input" value={recurso} onChange={(e) => setRecurso(e.target.value)}>
            <option value="">— ninguno de momento —</option>
            {(catalogos?.recursos ?? []).map((r) => (
              <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
            ))}
          </select>
        </Field>

        <div className="chips">
          <button
            className={`chip${esJefe ? ' chip--selected' : ''}`}
            disabled={sinRolJefe}
            title={sinRolJefe ? 'Antes hay que decir cuál es el rol de jefe en los parámetros' : ''}
            onClick={() => setEsJefe((v) => !v)}
          >
            {esJefe ? 'Será jefe de producción' : 'No es jefe de producción'}
          </button>
          {sinRolJefe ? (
            <span className="muted small" style={{ alignSelf: 'center' }}>
              Para poder marcarlo, elige antes el rol de jefe en Parámetros.
            </span>
          ) : null}
        </div>

        {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}
        <div className="row">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <div className="spacer" />
          <button
            className="btn btn--primary btn--lg"
            disabled={guardando || nombre.trim().length < 2 || apellidos.trim().length < 2}
            onClick={() => void crear()}
          >
            {guardando ? 'Creando en SAP…' : 'Crear en SAP'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function PinManual({
  operario,
  onClose,
  onGuardar,
}: {
  operario: OperarioTerminal;
  onClose: () => void;
  onGuardar: (pin: string) => void;
}) {
  const [pin, setPin] = useState('');
  const valido = /^\d{4,15}$/.test(pin);
  return (
    <Dialog title={`PIN de ${operario.fullName}`} subtitle="Numérico, de 4 a 15 dígitos. Se guarda en SAP." onClose={onClose}>
      <div className="stack">
        <Field label="PIN">
          <input
            className="input mono"
            style={{ fontSize: 30, letterSpacing: '0.2em', textAlign: 'center' }}
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 15))}
            onKeyDown={(e) => e.key === 'Enter' && valido && onGuardar(pin)}
          />
        </Field>
        <div className="row">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <div className="spacer" />
          <button className="btn btn--primary btn--lg" disabled={!valido} onClick={() => onGuardar(pin)}>
            Guardar en SAP
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function Envios({ puedeReintentar }: { puedeReintentar: boolean }) {
  const [items, setItems] = useState<Awaited<ReturnType<typeof direccionApi.outbox>> | null>(null);
  const cargar = useCallback(() => void direccionApi.outbox().then(setItems).catch(() => undefined), []);
  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 10_000);
    return () => clearInterval(t);
  }, [cargar]);

  if (!items) return <Empty icon="⏳" title="Cargando…" />;
  if (items.items.length === 0) {
    return <Empty icon="📭" title="No hay envíos registrados" body="Aquí aparecerá cada emisión y cada recibo que el terminal manda a SAP." />;
  }

  return (
    <section className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="table table--zebra">
        <thead>
          <tr>
            <th>Estado</th>
            <th>Documento</th>
            <th>Nº en SAP</th>
            <th>Error</th>
            {puedeReintentar ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {items.items.map((i) => (
            <tr key={i.id}>
              <td>
                <Pill tone={i.status === 'done' ? 'ok' : i.status === 'failed' ? 'danger' : 'warn'}>
                  {i.status === 'done' ? 'Contabilizado' : i.status === 'failed' ? 'Con error' : 'En cola'}
                </Pill>
              </td>
              <td>
                {i.summary}
                <div className="small muted">{new Date(i.createdAt).toLocaleString('es-ES')}</div>
              </td>
              <td className="mono">{i.sapDocNum ?? '—'}</td>
              <td className="small" style={{ maxWidth: 420, color: 'var(--danger)' }}>{i.lastError ?? ''}</td>
              {puedeReintentar ? (
                <td>
                  {i.status === 'failed' ? (
                    <button className="btn" onClick={() => void direccionApi.retry(i.id).then(cargar)}>
                      Reintentar
                    </button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function Usuarios({ rol }: { rol: 'director' | 'jefe' | 'operario' }) {
  const [users, setUsers] = useState<UsuarioTerminal[]>([]);
  const [puedeCrear, setPuedeCrear] = useState<('director' | 'jefe' | 'operario')[]>([]);
  const [nuevo, setNuevo] = useState(false);
  const [credencial, setCredencial] = useState<{ username: string; password: string; pin?: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [baja, setBaja] = useState<UsuarioTerminal | null>(null);
  const [cambiando, setCambiando] = useState<UsuarioTerminal | null>(null);

  const cargar = useCallback(
    () =>
      void direccionApi
        .users()
        .then((r) => {
          setUsers(r.users);
          setPuedeCrear(r.puedeCrear);
        })
        .catch((e: Error) => setError(e.message)),
    [],
  );
  useEffect(cargar, [cargar]);

  const gestiona = rol === 'director' || rol === 'jefe';
  if (!gestiona) {
    return <Empty icon="🔒" title="Tu usuario es de consulta" body="La gestión de usuarios la llevan el responsable de planta y el jefe de producción." />;
  }

  // Un jefe no puede tocar a un director ni a otro jefe: la lista se lo dice
  // en vez de dejarle pulsar y darle un error.
  const puedeTocar = (u: UsuarioTerminal) => rol === 'director' || u.role === 'operario';

  return (
    <section className="card stack">
      <div className="row">
        <div>
          <div className="eyebrow">Usuarios de esta planta</div>
          <div className="muted small">
            {rol === 'director'
              ? 'Puedes dar de alta responsables, jefes y operarios.'
              : 'Como jefe de producción puedes dar de alta operarios y, de paso, su PIN del terminal.'}
          </div>
        </div>
        <div className="spacer" />
        <button className="btn btn--primary" onClick={() => setNuevo(true)}>
          {rol === 'director' ? '+ Nuevo usuario' : '+ Nuevo operario'}
        </button>
      </div>

      {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}

      <table className="table">
        <thead>
          <tr><th>Usuario</th><th>Nombre</th><th>Rol</th><th>Última entrada</th><th /></tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} style={u.active ? undefined : { opacity: 0.55 }}>
              <td className="mono">{u.username}</td>
              <td>{u.fullName}</td>
              <td>
                <Pill tone={u.role === 'director' ? 'accent' : 'default'}>
                  {u.role === 'director' ? 'Responsable de planta' : u.role === 'jefe' ? 'Jefe de producción' : 'Operario'}
                </Pill>
                {u.global ? <> <Pill tone="ok">ECINSA</Pill></> : null}
                {!u.active ? <> <Pill tone="warn">De baja</Pill></> : null}
              </td>
              <td className="small muted">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('es-ES') : 'nunca'}</td>
              <td>
                {puedeTocar(u) && !u.active ? (
                  <button
                    className="btn"
                    onClick={() =>
                      void direccionApi
                        .reactivar(u.id)
                        .then(cargar)
                        .catch((e: Error) => setError(e.message))
                    }
                  >
                    Volver a dar de alta
                  </button>
                ) : u.global ? (
                  // Su contraseña y su baja viven en el panel de ECINSA, no
                  // aquí: tocarlas desde un cliente daría una sensación falsa
                  // de haber cerrado un acceso que sigue abierto en los demás.
                  <span className="muted small">Cuenta de ECINSA, se gestiona desde su panel</span>
                ) : puedeTocar(u) ? (
                  <div className="row">
                    <button className="btn" onClick={() => setCambiando(u)}>
                      Cambiar contraseña
                    </button>
                    <button
                      className="btn btn--ghost"
                      onClick={() => setBaja(u)}
                    >
                      Dar de baja
                    </button>
                  </div>
                ) : (
                  <span className="muted small">Lo lleva el responsable de planta</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {cambiando ? (
        <Dialog title={`Contraseña de ${cambiando.fullName}`} onClose={() => setCambiando(null)}>
          <CambiarContrasena
            usuario={cambiando.username}
            onCancelar={() => setCambiando(null)}
            onGuardar={(password) =>
              void direccionApi
                .resetPassword(cambiando.id, password)
                .then((r) => {
                  setCredencial({ username: cambiando.username, password: r.password });
                  setCambiando(null);
                })
                .catch((e: Error) => {
                  setCambiando(null);
                  setError(e.message);
                })
            }
          />
        </Dialog>
      ) : null}

      {nuevo ? (
        <NuevoUsuario
          rolesPermitidos={puedeCrear}
          onClose={() => setNuevo(false)}
          onCreado={(c) => {
            setNuevo(false);
            setCredencial(c);
            cargar();
          }}
        />
      ) : null}

      {baja ? (
        <Dialog title={`Dar de baja a ${baja.fullName}`} onClose={() => setBaja(null)}>
          <div className="stack">
            <p className="muted small" style={{ margin: 0 }}>
              No podrá volver a entrar con <span className="mono">{baja.username}</span>. Sus acciones siguen en la
              auditoría, y su PIN del terminal no se toca: eso se quita en la pestaña Operarios.
            </p>
            <div className="row">
              <button className="btn" onClick={() => setBaja(null)}>Cancelar</button>
              <div className="spacer" />
              <button
                className="btn btn--danger btn--lg"
                onClick={() =>
                  void direccionApi
                    .darDeBaja(baja.id)
                    .then(() => {
                      setBaja(null);
                      cargar();
                    })
                    .catch((e: Error) => {
                      setBaja(null);
                      setError(e.message);
                    })
                }
              >
                Dar de baja
              </button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {credencial ? (
        <Dialog title="Credenciales generadas" onClose={() => setCredencial(null)}>
          <div className="stack">
            <p className="muted small" style={{ margin: 0 }}>
              Apúntalas ahora: no se vuelven a poder ver. De la contraseña solo se guarda su huella.
            </p>
            <div className="card card--flat stack">
              <Copiable etiqueta="Usuario" valor={credencial.username} />
              <Copiable etiqueta="Contraseña" valor={credencial.password} />
              {credencial.pin ? <Copiable etiqueta="PIN del terminal de taller" valor={credencial.pin} /> : null}
            </div>
            <button className="btn btn--primary btn--lg" onClick={() => setCredencial(null)}>Hecho</button>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

function NuevoUsuario({
  rolesPermitidos,
  onClose,
  onCreado,
}: {
  rolesPermitidos: ('director' | 'jefe' | 'operario')[];
  onClose: () => void;
  onCreado: (c: { username: string; password: string; pin?: string | null }) => void;
}) {
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'director' | 'jefe' | 'operario'>(
    rolesPermitidos.includes('jefe') ? 'jefe' : 'operario',
  );
  const [empleados, setEmpleados] = useState<OperarioTerminal[]>([]);
  const [empleadoId, setEmpleadoId] = useState<number | ''>('');
  const [darPin, setDarPin] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Solo hace falta la lista de empleados si se va a crear un operario: es el
  // único rol que además trabaja en el terminal.
  useEffect(() => {
    if (role !== 'operario') return;
    void direccionApi.operarios().then((r) => setEmpleados(r.operarios)).catch(() => undefined);
  }, [role]);

  const empleado = empleados.find((e) => e.employeeId === empleadoId);

  const crear = async () => {
    setGuardando(true);
    setError(null);
    try {
      const r = await direccionApi.createUser({
        username,
        fullName,
        role,
        employeeId: empleadoId === '' ? null : empleadoId,
      });
      let pin: string | null = null;
      // Si se ha enlazado con un empleado de SAP y se pide, se le da también su
      // PIN: es lo que de verdad necesita para trabajar en el terminal.
      if (role === 'operario' && empleadoId !== '' && darPin && empleado && !empleado.hasPin) {
        pin = (await direccionApi.asignarPin(empleadoId)).pin;
      }
      onCreado({ username: r.user.username, password: r.password, pin });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      title={rolesPermitidos.length === 1 ? 'Nuevo operario' : 'Nuevo usuario'}
      subtitle="La contraseña se genera sola y se enseña una sola vez."
      onClose={onClose}
      wide
    >
      <div className="stack">
        {rolesPermitidos.length > 1 ? (
          <Field label="Rol">
            <div className="chips">
              {([['director', 'Responsable de planta'], ['jefe', 'Jefe de producción'], ['operario', 'Operario']] as const)
                .filter(([k]) => rolesPermitidos.includes(k))
                .map(([k, l]) => (
                  <button key={k} className={`chip${role === k ? ' chip--selected' : ''}`} onClick={() => setRole(k)}>
                    {l}
                  </button>
                ))}
            </div>
          </Field>
        ) : null}

        {role === 'operario' ? (
          <>
            <Field label="Empleado de SAP (opcional, pero es lo que le deja imputar)">
              <select
                className="input"
                value={empleadoId}
                onChange={(e) => {
                  const id = e.target.value === '' ? '' : Number(e.target.value);
                  setEmpleadoId(id);
                  const emp = empleados.find((x) => x.employeeId === id);
                  if (emp) {
                    setFullName(emp.fullName);
                    if (username === '') {
                      setUsername(
                        emp.fullName
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .split(/\s+/)
                          .slice(0, 2)
                          .join('.'),
                      );
                    }
                  }
                }}
              >
                <option value="">— sin enlazar —</option>
                {empleados.map((e) => (
                  <option key={e.employeeId} value={e.employeeId}>
                    {e.fullName} (nº {e.employeeId}){e.hasPin ? ' · ya tiene PIN' : ''}
                    {e.resourceCodes.length === 0 ? ' · sin recurso' : ''}
                  </option>
                ))}
              </select>
            </Field>
            {empleado ? (
              empleado.hasPin ? (
                <p className="muted small" style={{ margin: 0 }}>
                  {empleado.fullName} ya tiene PIN. Si lo ha perdido, genérale uno nuevo en la pestaña Operarios.
                </p>
              ) : (
                <div className="chips">
                  <button className={`chip${darPin ? ' chip--selected' : ''}`} onClick={() => setDarPin((v) => !v)}>
                    {darPin ? 'Se le genera también el PIN del terminal' : 'Sin PIN de terminal'}
                  </button>
                </div>
              )
            ) : null}
            {empleado && empleado.resourceCodes.length === 0 ? (
              <div className="toast" style={{ boxShadow: 'none', borderColor: 'rgba(251,191,36,.5)' }}>
                ⚠️ {empleado.fullName} no está en ningún recurso de mano de obra de SAP. Con PIN podrá entrar al
                terminal, pero no podrá imputar en ninguna fase hasta que se le asigne uno (SAP → Recursos → Empleados).
              </div>
            ) : null}
          </>
        ) : null}

        <div className="grid-2">
          <Field label="Usuario"><input className="input mono" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="nombre.apellido" /></Field>
          <Field label="Nombre y apellidos"><input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} /></Field>
        </div>

        {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}
        <div className="row">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <div className="spacer" />
          <button
            className="btn btn--primary btn--lg"
            disabled={guardando || username.length < 2 || fullName.length < 2}
            onClick={() => void crear()}
          >
            {guardando ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function EntrarConUsuario({
  tenantName,
  onEntrar,
}: {
  tenantName: string;
  onEntrar: (s: { user: UsuarioTerminal; tenant: { slug: string; name: string } }) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);

  const entrar = async () => {
    setEntrando(true);
    try {
      const r = await direccionApi.login(username, password);
      setToken(r.token);
      onEntrar({ user: r.user, tenant: r.tenant });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setPassword('');
    } finally {
      setEntrando(false);
    }
  };

  return (
    <div className="app">
      <div className="content" style={{ display: 'grid', placeItems: 'center' }}>
        <div className="card stack" style={{ width: 'min(440px, 100%)' }}>
          <div className="eyebrow">{tenantName}</div>
          <h1 className="h1">Dirección de planta</h1>
          <p className="muted small" style={{ margin: 0 }}>
            Entra con tu usuario. El terminal de taller se usa con PIN; esto es para verlo todo desde tu equipo.
          </p>
          <Field label="Usuario">
            <input className="input mono" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} />
          </Field>
          <Field label="Contraseña">
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void entrar()}
            />
          </Field>
          {error ? <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div> : null}
          <button className="btn btn--primary btn--lg btn--block" disabled={entrando || !username || !password} onClick={() => void entrar()}>
            {entrando ? 'Entrando…' : 'Entrar'}
          </button>
          <a className="link small" href="/">← Ir al terminal de taller (PIN)</a>
        </div>
      </div>
    </div>
  );
}
